/**
 * Problemas do fluxo (`validation.py`) — erros impedem publicar, avisos não.
 * Cada um aponta um card, um bloco ou uma ligação: o builder marca no canvas
 * e a lista leva até ele.
 */
import { locateBlock } from './document';
import type { FlowDoc, FlowIssue } from './types';

export type IssueLevel = 'error' | 'warning';

export interface IssueIndex {
  groups: Map<string, IssueLevel>;
  blocks: Map<string, IssueLevel>;
  edges: Map<string, IssueLevel>;
  errors: number;
  warnings: number;
}

const worse = (a: IssueLevel | undefined, b: IssueLevel): IssueLevel =>
  a === 'error' || b === 'error' ? 'error' : 'warning';

function mark(map: Map<string, IssueLevel>, id: string | null | undefined, level: IssueLevel) {
  if (id) map.set(id, worse(map.get(id), level));
}

/**
 * O pior nível por card, bloco e ligação. Problema de ligação não pinta o
 * card de origem inteiro — só a ligação (e o bloco de onde ela sai).
 */
export function indexIssues(issues: readonly FlowIssue[], doc?: FlowDoc): IssueIndex {
  const index: IssueIndex = {
    groups: new Map(),
    blocks: new Map(),
    edges: new Map(),
    errors: 0,
    warnings: 0,
  };
  for (const i of issues) {
    const level: IssueLevel = i.severity;
    if (level === 'error') index.errors++;
    else index.warnings++;
    if (i.edge_id) {
      mark(index.edges, i.edge_id, level);
      continue;
    }
    mark(index.blocks, i.block_id, level);
    const groupId = i.group_id ?? (i.block_id && doc ? locateBlock(doc, i.block_id)?.group.id : null);
    mark(index.groups, groupId, level);
  }
  return index;
}

export type IssueTarget =
  | { kind: 'edge'; edgeId: string; groupId: string | null }
  | { kind: 'block'; groupId: string; blockId: string }
  | { kind: 'group'; groupId: string }
  | null;

/** Para onde o clique num problema leva. `null`: o fluxo como um todo. */
export function issueTarget(issue: FlowIssue, doc: FlowDoc): IssueTarget {
  const at = issue.block_id ? locateBlock(doc, issue.block_id) : null;
  if (issue.edge_id && doc.edges.some((e) => e.id === issue.edge_id))
    return { kind: 'edge', edgeId: issue.edge_id, groupId: issue.group_id ?? at?.group.id ?? null };
  if (at) return { kind: 'block', groupId: at.group.id, blockId: at.block.id };
  if (issue.group_id && doc.groups.some((g) => g.id === issue.group_id))
    return { kind: 'group', groupId: issue.group_id };
  return null;
}

/** Erros primeiro; dentro do nível, a ordem da API. */
export function sortIssues(issues: readonly FlowIssue[]): FlowIssue[] {
  return [...issues].sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1));
}

/** Os problemas que a API mandou junto do 422 `flow_not_publishable`. */
export function issuesFromContext(context: Record<string, unknown>): FlowIssue[] {
  const raw = context.issues;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (i): i is FlowIssue =>
      typeof i === 'object' &&
      i !== null &&
      (i.severity === 'error' || i.severity === 'warning') &&
      typeof i.message === 'string',
  );
}
